/* eslint-disable react/prop-types */
/**
 * Admin → Pick Lists — manage dropdown vocabularies (BD.Admin).
 *
 * CRUD for the generic `pick_lists` table (see pickListsService.js). Each row
 * is a single dropdown value keyed by `(table_name, field, value)` with a
 * display `label`, `sort_order`, and `active` flag. The backend inlines the
 * active values into `GET /metadata` as an `options` array, so the deal-detail
 * tabs render these vocabularies without hardcoding them — an admin can add /
 * reorder / deactivate values here without a code change.
 *
 * Layout is master–detail: the left pane groups the vocabularies by the form
 * (table) they belong to → field; selecting one shows its values on the right.
 * Each vocabulary carries a friendly name (e.g. "Payments → Payment Type")
 * plus its raw `table.field` identity so the admin knows exactly which dropdown
 * they're editing and where it appears. Add/edit are scoped to the selected
 * vocabulary — table_name/field are implied by the selection, never typed.
 * The left pane has a search box, since there are dozens of vocabularies across
 * a dozen forms and scrolling to find one was the slow part of this screen.
 *
 * SHARED VOCABULARIES
 * `state` and `county` live under the reserved table_name `_shared` (de-hub
 * migration 068) and serve EVERY form rather than one screen. They used to be
 * seeded once per venue table, so this nav listed "State" and "County" four
 * times over. They now appear once, in a "Shared (all forms)" group pinned to
 * the top of the nav.
 *
 * SCOPED VOCABULARIES (cascading dropdowns)
 * A value may be scoped to another field's value via (parent_field,
 * parent_value). Every `county` is scoped to its `state`: the county list spans
 * all 50 states + DC (3,143 county-equivalents), and 444 of those names exist in
 * more than one state, so an unscoped list would be both ambiguous and
 * unusable. For a scoped vocabulary this screen shows the scope as a column in
 * the value grid and as a required field in the add/edit modal; `parent_field`
 * itself is never typed — it comes from the vocabulary.
 *
 * Gating is UI/UX only — the de-hub `/pick_lists` endpoints are the real
 * boundary (`@requires_roles([BD.Admin])`, de/admin bypass). We redirect
 * non-admins to `/` as defense-in-depth for the route.
 */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
} from "@azure/msal-react";
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Form,
  Input,
  InputNumber,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
  notification,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router";
import useMeasure from "react-use-measure";

import {
  WigAntLayout,
  WigAntTable,
} from "@warwick-investment-group/wig-react-library";

import SignIn from "../../signIn";
import { AppContext } from "../../../AppContextProvider";
import usePermissions from "../../../hooks/usePermissions";
import pickListsService from "../../../services/pickListsService";
import { apiErrorText } from "../../../utils/apiError";
import { titleCase } from "../../../utils/metadataV2Columns";

const { Title, Text, Paragraph } = Typography;

// Reserved `table_name` for vocabularies that serve EVERY form rather than one
// screen — `state` and `county` (de-hub migration 068). Must match
// SHARED_PICK_LIST_TABLE in de-hub's broker_deals/model.py. These used to be
// duplicated once per venue table, which is why State and County appeared four
// times in this nav.
const SHARED_TABLE = "_shared";

// Friendly "form" names per table (the screen/entity a vocabulary belongs to).
// Falls back to titleCase(table) for anything not listed.
const FORM_LABELS = {
  [SHARED_TABLE]: "Shared (all forms)",
  deals: "Deals",
  payments: "Payments",
  wells: "Wells",
  lease_deeds: "Lease / Deed",
  lease_deed_recordings: "Lease / Deed Recordings",
  assignment_recordings: "Assignment Recordings",
  lease_deed_tracts: "Lease / Deed Tracts",
  well_tract_regulatory: "Well Tract Regulatory",
  deal_contact_log: "Contact Log",
  remarks: "Remarks",
};

// Friendly field (dropdown) names. Falls back to titleCase(field). Overrides
// are only for cases titleCase gets wrong.
const FIELD_LABELS = {
  op_non_op: "Operated / Non-Operated",
  method_of_delivery: "Method of Delivery",
  lease_deed_type: "Lease / Deed Type",
};

const formLabel = (table) => FORM_LABELS[table] || titleCase(table);
const fieldLabel = (field) => FIELD_LABELS[field] || titleCase(field);
const vocabKey = (table, field) => `${table}.${field}`;

// Vocabularies whose values must carry a category, and the categories they
// may take. Mirrors de-hub's CATEGORY_REQUIRED_PICK_LISTS (model.py), which
// enforces it: a deal_status saved without one is a 422 at
// ["body","category"]. Reports use the category to tell working deals from
// closed or dead ones (the "Open" quick filter, deal aging).
const CATEGORY_REQUIRED = {
  "deals.deal_status": ["open", "closed", "dead"],
};
const CATEGORY_LABELS = { open: "Open", closed: "Closed", dead: "Dead" };

/** The categories a vocabulary requires, or null when it takes none. */
const requiredCategories = (vocab) =>
  vocab ? CATEGORY_REQUIRED[`${vocab.table}.${vocab.field}`] || null : null;

const categoryLabel = (c) => CATEGORY_LABELS[c] || c;

// Modal form template — only the per-value fields; table_name/field come from
// the selected vocabulary. `active` defaults on; `sort_order` to 0.
// `parent_value` is only meaningful for a scoped vocabulary (county → state);
// `category` only for a vocabulary that requires one (deal status). Blank
// values are left out of the request body (buildBody), so other lists never
// send a category.
const EMPTY_VALUE = {
  value: "",
  label: "",
  sort_order: 0,
  active: true,
  parent_value: "",
  category: "",
};

/**
 * PickListFormModal — Add/Edit a single value within the selected vocabulary.
 * table_name/field are not edited here; the header states which dropdown the
 * value belongs to.
 */
const PickListFormModal = ({
  open,
  mode, // 'add' | 'edit'
  vocab, // { table, field, parentField } — the selected vocabulary
  initialValues,
  saving,
  onCancel,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  // A scoped vocabulary (e.g. county, scoped to state) needs its parent value
  // captured alongside the value — the same county name legitimately exists in
  // many states, so the pair is the identity.
  const parentField = vocab?.parentField || null;
  const categories = requiredCategories(vocab);

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(initialValues || EMPTY_VALUE);
    }
  }, [open, initialValues, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      await onSubmit(values, { setFieldError: form.setFields });
    } catch {
      // validation / onSubmit errors surfaced inline by AntD or by the parent.
    }
  };

  return (
    <Modal
      open={open}
      title={mode === "add" ? "Add value" : "Edit value"}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={saving}
      okText={mode === "add" ? "Create" : "Save"}
      destroyOnHidden
      width={520}
    >
      {vocab && (
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          {mode === "add" ? "Adding to" : "Editing"}:{" "}
          <Text strong>
            {formLabel(vocab.table)} → {fieldLabel(vocab.field)}
          </Text>{" "}
          <Text code>
            {vocab.table}.{vocab.field}
          </Text>
          {vocab.table === SHARED_TABLE && (
            <>
              {" "}
              <Tag color="blue">SHARED</Tag>
            </>
          )}
        </Paragraph>
      )}
      <Form form={form} layout="vertical" initialValues={EMPTY_VALUE}>
        <Form.Item
          label="Value"
          name="value"
          rules={[{ required: true, message: "Value is required" }]}
          tooltip="The stored value written to the row (what the API validates)"
        >
          <Input autoFocus placeholder="e.g. BONUS" />
        </Form.Item>

        {parentField && (
          <Form.Item
            label={`${fieldLabel(parentField)} (scope)`}
            name="parent_value"
            rules={[
              {
                required: true,
                message: `${fieldLabel(parentField)} is required for this list`,
              },
            ]}
            tooltip={`This list is scoped by ${fieldLabel(
              parentField
            )} — the value only appears in the dropdown once that ${fieldLabel(
              parentField
            )} is chosen on the row. The same value may repeat under different ${fieldLabel(
              parentField
            )} scopes.`}
          >
            <Input placeholder="e.g. OK" />
          </Form.Item>
        )}

        {categories && (
          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: "Category is required for this list" }]}
            extra="Reports use this to tell working deals from closed or dead ones."
          >
            <Select
              aria-label="Category"
              placeholder="Choose a category"
              options={categories.map((c) => ({ value: c, label: categoryLabel(c) }))}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Label"
          name="label"
          tooltip="Display text shown in the dropdown. Falls back to the value when blank."
        >
          <Input placeholder="Optional — defaults to the value" />
        </Form.Item>

        <Form.Item
          label="Sort order"
          name="sort_order"
          tooltip="Lower numbers sort first in the dropdown"
        >
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>

        <Form.Item label="Active" name="active" valuePropName="checked">
          <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const PickLists = () => {
  const navigate = useNavigate();
  const [ref, bounds] = useMeasure();
  const { token, getToken, isTokenExpired, user, theme } =
    useContext(AppContext);
  const { can } = usePermissions();
  const allowed = can("admin.pickLists");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Master–detail selection: the active "table.field" vocabulary.
  const [selectedKey, setSelectedKey] = useState(null);
  const [openForms, setOpenForms] = useState([]);
  // Side-panel search over the vocabulary nav. There are dozens of
  // vocabularies across a dozen forms, so scrolling to find one is the slow
  // part of using this screen.
  const [navQuery, setNavQuery] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add"); // 'add' | 'edit'
  const [editingRow, setEditingRow] = useState(null);
  const [saving, setSaving] = useState(false);

  // Redirect users who lack the permission (defense-in-depth for the route;
  // the API is the real boundary).
  useEffect(() => {
    if (!allowed) navigate("/", { replace: true });
  }, [allowed, navigate]);

  const getAuth = useCallback(async () => {
    const userOid = user?.idTokenClaims?.oid || user?.localAccountId;
    if (!userOid) throw new Error("OID not found in user claims.");
    let tk = token;
    if (!tk || isTokenExpired(tk)) tk = await getToken();
    return { token: tk, userOid };
  }, [token, getToken, isTokenExpired, user]);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMsg(null);
      const { token: tk, userOid } = await getAuth();
      const resp = await pickListsService.listPickLists({
        token: tk,
        userOid,
        includeInactive,
      });
      const data = resp?.data?.data;
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading pick lists:", err);
      setErrorMsg(err?.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuth, includeInactive]);

  useEffect(() => {
    if (allowed) refresh();
  }, [allowed, refresh]);

  // ---------------------------------------------------------------------
  // Group rows into forms → fields (vocabularies) for the left nav.
  // ---------------------------------------------------------------------
  const forms = useMemo(() => {
    // table -> field -> { count, parentField }
    const byTable = new Map();
    for (const r of rows || []) {
      if (!r?.table_name || !r?.field) continue;
      if (!byTable.has(r.table_name)) byTable.set(r.table_name, new Map());
      const fields = byTable.get(r.table_name);
      const prev = fields.get(r.field) || { count: 0, parentField: null };
      fields.set(r.field, {
        count: prev.count + 1,
        // A vocabulary is scoped if ANY of its values carries a parent_field.
        parentField: prev.parentField || r.parent_field || null,
      });
    }
    return [...byTable.entries()]
      .map(([table, fields]) => ({
        table,
        label: formLabel(table),
        shared: table === SHARED_TABLE,
        fields: [...fields.entries()]
          .map(([field, meta]) => ({
            key: vocabKey(table, field),
            field,
            label: fieldLabel(field),
            count: meta.count,
            parentField: meta.parentField,
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      }))
      // Shared vocabularies pin to the top — they apply everywhere, so they
      // read as a section header rather than as one screen among many.
      .sort((a, b) => {
        if (a.shared !== b.shared) return a.shared ? -1 : 1;
        return a.label.localeCompare(b.label);
      });
  }, [rows]);

  // Filter the nav by the side-panel search box. Matches a vocabulary on its
  // friendly label, its raw table.field identity, or its form's label — so
  // "county", "state", "well_tracts.location_type" and "Payments" all work.
  // A form whose own label matches keeps all of its fields.
  const filteredForms = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return forms;
    const out = [];
    for (const f of forms) {
      const formMatches =
        f.label.toLowerCase().includes(q) || f.table.toLowerCase().includes(q);
      const fields = formMatches
        ? f.fields
        : f.fields.filter(
            (fd) =>
              fd.label.toLowerCase().includes(q) ||
              fd.key.toLowerCase().includes(q) ||
              fd.field.toLowerCase().includes(q)
          );
      if (fields.length) out.push({ ...f, fields });
    }
    return out;
  }, [forms, navQuery]);

  // Flat list of vocabularies (for auto-select / lookup). Built from the
  // UNFILTERED forms so a search that hides the current selection doesn't
  // discard it — the right pane keeps showing what the admin was editing.
  const vocabularies = useMemo(
    () =>
      forms.flatMap((f) =>
        f.fields.map((fd) => ({
          key: fd.key,
          table: f.table,
          field: fd.field,
          parentField: fd.parentField,
        }))
      ),
    [forms]
  );

  const selected = useMemo(
    () => vocabularies.find((v) => v.key === selectedKey) || null,
    [vocabularies, selectedKey]
  );

  // Auto-select the first vocabulary once data loads (or if the selection
  // disappears after a refresh).
  useEffect(() => {
    if (!vocabularies.length) return;
    if (!selectedKey || !vocabularies.some((v) => v.key === selectedKey)) {
      setSelectedKey(vocabularies[0].key);
    }
  }, [vocabularies, selectedKey]);

  // Open all form groups on first load; preserve the admin's toggles after.
  useEffect(() => {
    setOpenForms((prev) =>
      prev.length ? prev : forms.map((f) => `form:${f.table}`)
    );
  }, [forms]);

  const menuItems = useMemo(
    () =>
      filteredForms.map((f) => ({
        key: `form:${f.table}`,
        label: f.label,
        children: f.fields.map((fd) => ({
          key: fd.key,
          label: `${fd.label} (${fd.count})`,
        })),
      })),
    [filteredForms]
  );

  // While searching, force every matching group open — a hit buried in a
  // collapsed group looks like no result at all.
  const effectiveOpenForms = useMemo(
    () =>
      navQuery.trim()
        ? filteredForms.map((f) => `form:${f.table}`)
        : openForms,
    [navQuery, filteredForms, openForms]
  );

  // Values for the active vocabulary, ordered like the dropdown. A scoped
  // vocabulary restarts sort_order within each parent (county numbers from 1 in
  // every state), so parent_value leads the sort — otherwise the grid
  // interleaves states.
  const valueRows = useMemo(() => {
    if (!selected) return [];
    return (rows || [])
      .filter(
        (r) => r?.table_name === selected.table && r?.field === selected.field
      )
      .sort((a, b) => {
        const p = String(a?.parent_value ?? "").localeCompare(
          String(b?.parent_value ?? "")
        );
        if (p !== 0) return p;
        const s = (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
        if (s !== 0) return s;
        return String(a?.value ?? "").localeCompare(String(b?.value ?? ""));
      });
  }, [rows, selected]);

  // ---------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------

  // Per-value fields the form owns. table_name/field come from `selected` on
  // create and are immutable on update.
  const VALUE_KEYS = Object.keys(EMPTY_VALUE);

  const buildBody = (values, keys) =>
    Object.fromEntries(
      Object.entries(values || {}).filter(
        ([k, v]) =>
          keys.includes(k) &&
          v !== undefined &&
          !(typeof v === "string" && v.trim() === "" && k !== "value")
      )
    );

  const handleCreate = async (values, helpers) => {
    if (!selected) return;
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await pickListsService.createPickList({
        token: tk,
        userOid,
        body: {
          ...buildBody(values, VALUE_KEYS),
          table_name: selected.table,
          field: selected.field,
          // For a scoped vocabulary the API requires parent_field and
          // parent_value TOGETHER (422 otherwise). The admin only types the
          // value; the field comes from the vocabulary itself, so it is
          // supplied here rather than being a form input.
          ...(selected.parentField
            ? { parent_field: selected.parentField }
            : {}),
        },
      });
      message.success("Pick-list value created");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (values, helpers) => {
    setSaving(true);
    try {
      const { token: tk, userOid } = await getAuth();
      await pickListsService.updatePickList({
        token: tk,
        userOid,
        id: editingRow.id,
        body: buildBody(values, VALUE_KEYS),
      });
      message.success("Pick-list value updated");
      setModalOpen(false);
      await refresh();
    } catch (err) {
      handleSaveError(err, helpers);
    } finally {
      setSaving(false);
    }
  };

  // Surface a save error inline on the field it is about — `value` for the
  // 409 duplicate case, and any 422 entry whose `loc` names a field on the
  // form (e.g. ["body","category"] for a deal status without a category) —
  // or as a top-level notification otherwise.
  const handleSaveError = (err, helpers) => {
    const status = err?.response?.status;
    const detail = apiErrorText(err, { fallback: "Save failed" });

    if (status === 409) {
      helpers?.setFieldError?.([{ name: "value", errors: [String(detail)] }]);
      return;
    }
    const entries = err?.response?.data?.detail;
    if (status === 422 && Array.isArray(entries)) {
      const onForm = entries
        .map((e) => ({ name: (e?.loc || []).filter((p) => typeof p === "string").pop(), msg: e?.msg }))
        .filter((e) => VALUE_KEYS.includes(e.name) && e.msg);
      if (onForm.length && onForm.length === entries.length) {
        helpers?.setFieldError?.(onForm.map((e) => ({ name: e.name, errors: [String(e.msg)] })));
        return;
      }
    }
    notification.error({
      message: "Pick-list save failed",
      description: String(detail),
    });
  };

  const handleDelete = async (row) => {
    try {
      const { token: tk, userOid } = await getAuth();
      await pickListsService.deletePickList({
        token: tk,
        userOid,
        id: row.id,
      });
      message.success("Pick-list value deleted");
      await refresh();
    } catch (err) {
      const detail = apiErrorText(err);
      notification.error({
        message: "Failed to delete pick-list value",
        description: detail,
      });
    }
  };

  // ---------------------------------------------------------------------
  // Grid wiring
  // ---------------------------------------------------------------------

  const openAdd = () => {
    setModalMode("add");
    setEditingRow(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setModalMode("edit");
    setEditingRow(row);
    setModalOpen(true);
  };

  const columns = useMemo(
    () => [
      {
        dataIndex: "value",
        title: "Value",
        key: "value",
        sorter: (a, b) =>
          String(a?.value ?? "").localeCompare(String(b?.value ?? "")),
      },
      // Scope column, shown only for a scoped vocabulary. Without it a county
      // list is thousands of rows in which the same name appears many times
      // with nothing to tell them apart.
      ...(selected?.parentField
        ? [
            {
              dataIndex: "parent_value",
              title: fieldLabel(selected.parentField),
              key: "parent_value",
              width: 120,
              defaultSortOrder: "ascend",
              sorter: (a, b) =>
                String(a?.parent_value ?? "").localeCompare(
                  String(b?.parent_value ?? "")
                ),
              render: (value) =>
                value ? (
                  <Tag>{value}</Tag>
                ) : (
                  <Text type="secondary" italic>
                    —
                  </Text>
                ),
            },
          ]
        : []),
      // Category column, only for a vocabulary that requires one (deal
      // status). A value still missing its category shows a warning tag: it
      // must get one on its next edit.
      ...(requiredCategories(selected)
        ? [
            {
              dataIndex: "category",
              title: "Category",
              key: "category",
              width: 120,
              sorter: (a, b) => String(a?.category ?? "").localeCompare(String(b?.category ?? "")),
              render: (value) =>
                value ? <Tag>{categoryLabel(value)}</Tag> : <Tag color="warning">None</Tag>,
            },
          ]
        : []),
      {
        dataIndex: "label",
        title: "Label",
        key: "label",
      },
      {
        dataIndex: "sort_order",
        title: "Sort",
        key: "sort_order",
        width: 90,
        align: "right",
        sorter: (a, b) => (a?.sort_order ?? 0) - (b?.sort_order ?? 0),
      },
      {
        dataIndex: "active",
        title: "Active",
        key: "active",
        width: 100,
        sorter: (a, b) => Number(a?.active) - Number(b?.active),
        render: (value) => (
          <Tag color={value ? "green" : "default"}>
            {value ? "ACTIVE" : "INACTIVE"}
          </Tag>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 130,
        fixed: "right",
        align: "center",
        render: (_, row) => {
          if (!row) return null;
          return (
            <Space size="small" onClick={(e) => e.stopPropagation()}>
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEdit(row)}
                aria-label={`Edit ${row.value}`}
              />
              <Popconfirm
                title="Delete value?"
                description={`Delete "${row.value}"? This cannot be undone.`}
                okText="Delete"
                okType="danger"
                cancelText="Cancel"
                onConfirm={() => handleDelete(row)}
              >
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  aria-label={`Delete ${row.value}`}
                />
              </Popconfirm>
            </Space>
          );
        },
      },
    ],
    // Depends on the selected vocabulary's parent field: the scope column is
    // present only for a scoped vocabulary, so switching between County
    // (scoped) and, say, Payment Type (unscoped) has to rebuild the columns.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected]
  );

  const modalInitialValues = useMemo(() => {
    if (modalMode === "edit" && editingRow) {
      return VALUE_KEYS.reduce((acc, k) => {
        acc[k] = editingRow[k] ?? EMPTY_VALUE[k];
        return acc;
      }, {});
    }
    return EMPTY_VALUE;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalMode, editingRow]);

  const gridHeight = Math.max(bounds.height - 240, 360);

  if (!allowed) return null;

  return (
    <React.Fragment>
      <AuthenticatedTemplate>
        <Row ref={ref}>
          <WigAntLayout
            darkMode={theme === "dark"}
            layoutStyle={{
              height: "93vh",
              width: "99vw",
              margin: 0,
              padding: 0,
              overflow: "hidden",
            }}
            contentStyle={{
              minHeight: "70vh",
              overflow: "auto",
              padding: 12,
              margin: 0,
            }}
            showFooter={false}
            showHeader={false}
            siderProps={{ collapsible: true, collapsed: true, width: 0 }}
            showDefaultMenu={false}
            content={
              <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Row justify="space-between" align="middle">
                  <Title level={3} style={{ margin: 0 }}>
                    Pick Lists
                  </Title>
                  <Checkbox
                    checked={includeInactive}
                    onChange={(e) => setIncludeInactive(e.target.checked)}
                  >
                    Show inactive
                  </Checkbox>
                </Row>

                {errorMsg && (
                  <Alert
                    type="error"
                    showIcon
                    message="Failed to load pick lists"
                    description={errorMsg}
                  />
                )}

                <div style={{ display: "flex", gap: 16, width: "100%" }}>
                  {/* Left: vocabularies grouped by form → field, with the
                      shared (all-forms) group pinned to the top. */}
                  <div
                    data-testid="vocab-nav"
                    style={{
                      flex: "0 0 280px",
                      maxWidth: 280,
                      display: "flex",
                      flexDirection: "column",
                      maxHeight: gridHeight + 60,
                      borderRight: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    {/* Search stays pinned while the list below scrolls. */}
                    <Input
                      allowClear
                      size="small"
                      prefix={<SearchOutlined />}
                      placeholder="Search dropdowns…"
                      value={navQuery}
                      onChange={(e) => setNavQuery(e.target.value)}
                      aria-label="Search dropdowns"
                      style={{ marginBottom: 8, flex: "0 0 auto" }}
                    />
                    <div style={{ overflow: "auto", flex: "1 1 auto" }}>
                      {menuItems.length === 0 ? (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            navQuery.trim()
                              ? `No dropdowns match "${navQuery.trim()}"`
                              : includeInactive
                                ? "No vocabularies yet"
                                : "No active vocabularies"
                          }
                        />
                      ) : (
                        <Menu
                          mode="inline"
                          items={menuItems}
                          selectedKeys={selectedKey ? [selectedKey] : []}
                          openKeys={effectiveOpenForms}
                          onOpenChange={setOpenForms}
                          onClick={({ key }) => setSelectedKey(key)}
                          style={{ borderInlineEnd: "none" }}
                        />
                      )}
                    </div>
                  </div>

                  {/* Right: values for the selected vocabulary. */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    {!selected ? (
                      <Empty description="Select a dropdown on the left to manage its values." />
                    ) : (
                      <Space
                        direction="vertical"
                        size={12}
                        style={{ width: "100%" }}
                      >
                        <Row justify="space-between" align="middle">
                          <Space direction="vertical" size={0}>
                            <Title level={4} style={{ margin: 0 }}>
                              {formLabel(selected.table)} →{" "}
                              {fieldLabel(selected.field)}
                            </Title>
                            <Text type="secondary">
                              Values for the{" "}
                              <Text strong>{fieldLabel(selected.field)}</Text>{" "}
                              dropdown{" "}
                              <Text code>
                                {selected.table}.{selected.field}
                              </Text>
                            </Text>
                          </Space>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openAdd}
                          >
                            Add value
                          </Button>
                        </Row>

                        <WigAntTable
                          dataSource={valueRows}
                          columns={columns}
                          rowKey={(record) =>
                            record?.id != null ? String(record.id) : undefined
                          }
                          loading={loading}
                          showSearch
                          searchWidth={300}
                          showActionButton={false}
                          showExport
                          exportOptions={{
                            filename: `pick_list_${selected.table}_${selected.field}`,
                          }}
                          showPagination
                          pagination={{
                            defaultPageSize: 100,
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "25", "50", "100"],
                          }}
                          tableWidth="100%"
                          scroll={{ y: gridHeight }}
                        />
                      </Space>
                    )}
                  </div>
                </div>

                <PickListFormModal
                  open={modalOpen}
                  mode={modalMode}
                  vocab={selected}
                  initialValues={modalInitialValues}
                  saving={saving}
                  onCancel={() => setModalOpen(false)}
                  onSubmit={modalMode === "add" ? handleCreate : handleUpdate}
                />
              </Space>
            }
          />
        </Row>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <SignIn />
      </UnauthenticatedTemplate>
    </React.Fragment>
  );
};

export default PickLists;
